import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { navSites } from "@/mocks/navSites";
import { categories } from "@/mocks/navCategories";

interface NavbarProps {
  overlay?: boolean;
}

export default function Navbar({ overlay = false }: NavbarProps) {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
    setKeyword("");
  };

  const solid = !overlay || scrolled;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
          : "bg-primary-900/10"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 shrink-0 group cursor-pointer"
          >
            <span
              className={`w-9 h-9 rounded-lg flex items-center justify-center font-heading font-extrabold text-lg leading-none ${
                solid
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-50 text-primary-600"
              }`}
            >
              51
            </span>
            <span
              className={`font-heading font-bold text-lg whitespace-nowrap ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              51网址导航
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                solid
                  ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                  : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"
              }`}
            >
              首页
            </Link>
            <div className="relative group">
              <button
                type="button"
                className={`px-3 py-2 rounded-md text-sm flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap ${
                  solid
                    ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                    : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"
                }`}
              >
                分类
                <i className="ri-arrow-down-s-line text-base" />
              </button>
              <div className="absolute left-0 top-full pt-2 hidden group-hover:block">
                <div className="w-[440px] grid grid-cols-2 gap-1 p-3 rounded-lg bg-background-50 border border-background-200">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="w-6 h-6 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className={`${cat.icon} text-sm`} />
                      </span>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className={`px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                solid
                  ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                  : "text-background-50/90 hover:text-background-50 hover:bg-background-50/10"
              }`}
            >
              关于
            </Link>
          </nav>

          <form
            onSubmit={submitSearch}
            className="hidden md:flex flex-1 max-w-[420px] items-center relative"
          >
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索网站名称、网址或关键词"
              className="w-full h-9 pl-9 pr-[76px] rounded-md text-sm bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <button
              type="submit"
              className="absolute right-1 top-1 h-7 px-3 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <Link
              to="/category/entertainment"
              className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap"
            >
              热门分类
            </Link>
            <Link
              to="/about#feedback"
              className="px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              提交网址
            </Link>
          </div>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((prev) => !prev)}
            className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer ${
              solid
                ? "text-foreground-800 bg-background-100"
                : "text-background-50 bg-background-50/15"
            }`}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 animate-fade-in">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="relative mb-4">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索网站..."
                className="w-full h-10 pl-9 pr-16 rounded-md bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                className="absolute right-1 top-1 h-8 px-3 rounded-md bg-primary-500 text-background-50 text-xs cursor-pointer whitespace-nowrap"
              >
                搜索
              </button>
            </form>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-2 py-2 rounded-md text-sm text-foreground-700 bg-background-100 cursor-pointer"
                >
                  <i className={`${cat.icon} text-primary-500`} />
                  {cat.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex-1 h-9 flex items-center justify-center rounded-md bg-background-100 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                首页
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                className="flex-1 h-9 flex items-center justify-center rounded-md bg-background-100 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                关于我们
              </Link>
              <Link
                to="/about#feedback"
                onClick={() => setMobileOpen(false)}
                className="flex-1 h-9 flex items-center justify-center rounded-md bg-accent-500 text-sm text-background-50 cursor-pointer whitespace-nowrap"
              >
                提交网址
              </Link>
            </div>
            <p className="mt-4 text-xs text-foreground-400">
              共收录 {navSites.length} 个精选网站 · 数据为演示内容
            </p>
          </div>
        </div>
      )}
    </header>
  );
}